import type { CaptureTemplateRequest, CreateGeoSiteRequest, FreeDomainsView, SiteSetupView, SiteStructureView, StructureStringsRequest } from '@/types/api';
import type { BootstrapCredential, CloneSiteRequest, CreateFilesSiteRequest, CreateSiteRequest, EnqueueResult, MigrateSiteRequest, ObserveSiteRequest, RelocateSiteRequest, SiteActivityView, SiteBackupView, SiteCommandRequest, SiteDetail, SiteOverview, SitePlatformRequest, SiteSyncView } from '@/types/api';
import { apiPage, apiRequest, apiUpload, withQuery } from '@/api/client';

export interface SiteFilter {
  search?: string;
  tagIds?: readonly string[];
  untagged?: boolean;
  status?: string;
  engine?: string;
  tier?: string;
  /** On one server (ADR-0058). */
  serverId?: string;
  cursor?: string | null;
  limit?: number;
}

function siteListPath(filter: SiteFilter): string {
  const params = new URLSearchParams();
  if (filter.search) params.set('search', filter.search);

  // "Without tags" and "with these tags" are different questions, never both.
  if (filter.untagged) params.set('untagged', 'true');
  else for (const id of filter.tagIds ?? []) params.append('tag', id);

  if (filter.status) params.set('status', filter.status);
  if (filter.engine) params.set('engine', filter.engine);
  if (filter.tier) params.set('tier', filter.tier);
  if (filter.serverId) params.set('serverId', filter.serverId);
  if (filter.cursor) params.set('cursor', filter.cursor);
  if (filter.limit) params.set('limit', String(filter.limit));

  return withQuery('/sites', params);
}

function cursorQuery(limit: number, cursor: string | null | undefined): string {
  return `?limit=${limit}${cursor ? `&cursor=${cursor}` : ''}`;
}

/** Sites: the list, one site, and everything that can be done to one. */
export const sitesApi = {
  list: (filter: SiteFilter = {}) => apiPage<SiteOverview>(siteListPath(filter)),

  get: (id: string) => apiRequest<SiteDetail>(`/sites/${id}`),

  activity: (id: string, cursor?: string | null) =>
    apiPage<SiteActivityView>(`/sites/${id}/activity${cursorQuery(30, cursor)}`),

  backups: (id: string, cursor?: string | null) =>
    apiPage<SiteBackupView>(`/sites/${id}/backups${cursorQuery(20, cursor)}`),

  create: (input: CreateSiteRequest, key: string) =>
    apiRequest<EnqueueResult & { siteId: string }>('/sites', { method: 'POST', body: input, idempotencyKey: key }),

  /** A new site of files on an external server (ADR-0044). */
  createFiles: (input: CreateFilesSiteRequest, key: string) =>
    apiRequest<EnqueueResult & { siteId: string }>('/sites/files', { method: 'POST', body: input, idempotencyKey: key }),

  /** The archive a new site of files is made from, sent before the site is asked for. */
  uploadArchive: (uploadId: string, file: Blob, sha256: string) =>
    apiUpload<{ uploadId: string; sizeBytes: number }>(`/site-uploads/${uploadId}`, file, sha256),

  clone: (id: string, input: CloneSiteRequest, key: string) =>
    apiRequest<EnqueueResult & { siteId: string }>(`/sites/${id}/clone`, { method: 'POST', body: input, idempotencyKey: key }),

  migrate: (id: string, input: MigrateSiteRequest, key: string) =>
    apiRequest<EnqueueResult>(`/sites/${id}/migrate`, { method: 'POST', body: input, idempotencyKey: key }),

  /** Raises a site whose server is gone on another one, from a backup (P8). */
  relocate: (id: string, input: RelocateSiteRequest, key: string) =>
    apiRequest<EnqueueResult>(`/sites/${id}/relocate`, { method: 'POST', body: input, idempotencyKey: key }),

  /** The 2d2s tag whose domains become watched sites (ADR-0030). */
  sync: () => apiRequest<SiteSyncView>('/sites/sync'),

  /** A pass over the tagged domains now, rather than at the next scheduled one. */
  syncTagged: (key: string) => apiRequest<EnqueueResult>('/sites/sync', { method: 'POST', idempotencyKey: key }),

  /** An operator's word on the platform and the admin address; null hands a field back to detection. */
  setPlatform: (id: string, input: SitePlatformRequest) =>
    apiRequest<{ ok: boolean }>(`/sites/${id}/platform`, { method: 'PUT', body: input }),

  /** A new picture of the front page within minutes (ADR-0034). */
  requestPreview: (id: string) => apiRequest<{ requested: boolean }>(`/sites/${id}/preview`, { method: 'POST' }),

  /** Looks at the site's pages again within a minute. */
  detectPlatform: (id: string) => apiRequest<{ queued: boolean }>(`/sites/${id}/platform/detect`, { method: 'POST' }),

  observe: (input: ObserveSiteRequest) =>
    apiRequest<{ siteId: string }>('/sites/observed', { method: 'POST', body: input }),

  unobserve: (id: string, confirmation: string) =>
    apiRequest<{ removed: boolean }>(`/sites/${id}/unobserve`, { method: 'POST', body: { confirmation } }),

  /** The first administrator's password, shown once. */
  revealBootstrapCredential: (id: string) =>
    apiRequest<BootstrapCredential>(`/sites/${id}/bootstrap-credential`, { method: 'POST' }),

  /** Deploy, restart, backup, restore, destroy, upgrade, domains: each queues a job. */
  operate: (id: string, command: SiteCommandRequest, key: string) =>
    apiRequest<EnqueueResult>(`/sites/${id}/operations`, { method: 'POST', body: command, idempotencyKey: key }),

  setTags: (id: string, tagIds: string[]) =>
    apiRequest<{ ok: boolean }>(`/sites/${id}/tags`, { method: 'PUT', body: { tagIds } }),

  /** A new GEO from a template build (ADR-0047). */
  createGeo: (input: CreateGeoSiteRequest, key: string) =>
    apiRequest<EnqueueResult & { siteId: string }>('/sites/geo', { method: 'POST', body: input, idempotencyKey: key }),

  /** A site's creation, step by step. */
  setup: (id: string) => apiRequest<SiteSetupView>(`/sites/${id}/setup`),

  /** The site's template structure and its translations. */
  structure: (id: string) => apiRequest<SiteStructureView>(`/sites/${id}/structure`),

  saveStructure: (id: string, input: StructureStringsRequest, key: string) =>
    apiRequest<EnqueueResult>(`/sites/${id}/structure`, { method: 'PUT', body: input, idempotencyKey: key }),

  /** A new GEO opened to search engines. */
  launch: (id: string, key: string) => apiRequest<EnqueueResult>(`/sites/${id}/launch`, { method: 'POST', idempotencyKey: key }),

  /** The site becomes a template, or its template's next version. */
  captureTemplate: (id: string, input: CaptureTemplateRequest, key: string) =>
    apiRequest<EnqueueResult>(`/sites/${id}/template-capture`, { method: 'POST', body: input, idempotencyKey: key }),

  /** Domains a new site can take. */
  freeDomains: () => apiRequest<FreeDomainsView>('/domains/free'),
};
